import {
  CalendarDays,
  MapPin,
  Route,
  UserPlus,
  Users,
} from 'lucide-react';
import { PublicContentPage, publicPageHeading } from '../../features/public/PublicContentPage';
import { PublicCta } from '../../features/public/PublicCta';
import { seoForPath } from '../../features/public/seoConfig';

const COMMUNITY_FEATURES = [
  {
    icon: UserPlus,
    title: 'Find and connect with riders',
    body: 'Discover fellow motorcycle enthusiasts and grow your riding circle beyond scattered chat groups.',
  },
  {
    icon: Users,
    title: 'Create and join riding groups',
    body: 'Bring your club, friends, or weekend crew together in one place built for group riding.',
  },
] as const;

const RIDE_FEATURES = [
  {
    icon: Route,
    title: 'Organize group rides',
    body: 'Plan routes, meeting points, and ride details so every rider knows the plan before wheels roll.',
  },
  {
    icon: CalendarDays,
    title: 'Plan and participate in rides',
    body: 'Schedule upcoming rides, join with a tap, and stay aligned from kick-off to finish.',
  },
  {
    icon: MapPin,
    title: 'Manage riding communities',
    body: 'Keep your group organized with roles, updates, and ride coordination tools made for the road.',
  },
] as const;

export function FeaturesPage() {
  return (
    <PublicContentPage seo={seoForPath('/features')}>
      {publicPageHeading(
        'Motorcycle group ride app features',
        'RideMaadi brings ride planning, group coordination, and community tools together in one motorcycle riding app — built for riders and riding groups.',
      )}
      <section className="space-y-4" aria-labelledby="community-features">
        <h2 id="community-features" className="text-lg font-semibold">
          Rider and community features
        </h2>
        <ul className="grid gap-4 sm:grid-cols-2">
          {COMMUNITY_FEATURES.map(({ icon: Icon, title, body }) => (
            <li
              key={title}
              className="rounded-2xl bg-surface p-5 shadow-sm shadow-black/[0.04] dark:shadow-black/20"
            >
              <Icon className="h-6 w-6 text-primary" aria-hidden />
              <h3 className="mt-3 text-base font-semibold">{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted">{body}</p>
            </li>
          ))}
        </ul>
      </section>
      <section className="mt-10 space-y-4" aria-labelledby="ride-features">
        <h2 id="ride-features" className="text-lg font-semibold">
          Ride planning and coordination
        </h2>
        <ul className="grid gap-4 sm:grid-cols-2">
          {RIDE_FEATURES.map(({ icon: Icon, title, body }) => (
            <li
              key={title}
              className="rounded-2xl bg-surface p-5 shadow-sm shadow-black/[0.04] dark:shadow-black/20"
            >
              <Icon className="h-6 w-6 text-primary" aria-hidden />
              <h3 className="mt-3 text-base font-semibold">{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted">{body}</p>
            </li>
          ))}
        </ul>
      </section>
      <div className="mt-12">
        <PublicCta heading="Try RideMaadi with your riding group" />
      </div>
    </PublicContentPage>
  );
}
